import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CornerUpLeft, AlertTriangle, CheckCircle2, Circle, FileText, Scale } from 'lucide-react';
import { apiService } from '@/lib/api';
import LenderShell, { LENDER_BLUE, LENDER_NAVY } from '@/components/lender/LenderShell';
import { getLenderActor, money, fmtDateTime, DRAW_STATUS } from '@/lib/lenderDemo';

const EVENT_LABEL = {
  submitted: 'Submitted', resubmitted: 'Resubmitted', notified_lender: 'Lender notified',
  viewed: 'Viewed', sent_back: 'Sent back', lo_approved: 'Loan officer approved',
  final_approved: 'Final approval', outstanding_recorded: 'Outstanding items recorded',
  budget_flag: 'Budget check',
};

export default function LenderDrawReview() {
  const { drawId } = useParams();
  const navigate = useNavigate();
  const actor = getLenderActor();
  const [d, setD] = useState(null);
  const [note, setNote] = useState('');
  const [showSendBack, setShowSendBack] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!actor?.id) return;
    apiService.lenderDrawDetail(actor.id, drawId).then(({ data }) => setD(data)).catch(() => setD(false));
  }, [actor?.id, drawId]);
  useEffect(() => { load(); }, [load]);

  if (d === false) return <LenderShell active="draws"><p className="text-slate-400">Draw not found or outside your access.</p></LenderShell>;
  if (!d) return <LenderShell active="draws"><p className="text-slate-400">Loading…</p></LenderShell>;

  const draw = d.draw;
  const chip = DRAW_STATUS[draw.status] || DRAW_STATUS.submitted;
  const missing = draw.missing_docs || [];
  const lines = draw.line_items || [];
  const hasBidSheet = lines.some((li) => li.approved_budget != null);
  const contUsed = Number(draw.contingency_used || 0);
  const uncovered = Number(draw.uncovered_over || 0);
  const totals = lines.reduce((t, li) => ({
    approved: t.approved + Number(li.approved_budget || 0),
    drawn: t.drawn + Number(li.drawn_to_date || 0),
    thisDraw: t.thisDraw + Number(li.this_draw != null ? li.this_draw : (li.amount || 0)),
  }), { approved: 0, drawn: 0, thisDraw: 0 });

  const approve = async () => {
    setBusy(true);
    try { await apiService.lenderApproveDraw(actor.id, drawId, note); navigate('/lender/draws'); }
    finally { setBusy(false); }
  };
  const sendBack = async () => {
    setBusy(true);
    try { await apiService.lenderSendBack(actor.id, drawId, note || 'Sent back for corrections'); navigate('/lender/draws'); }
    finally { setBusy(false); }
  };

  return (
    <LenderShell active="draws">
      <button onClick={() => navigate('/lender/draws')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to draws
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>{draw.borrower_name} · Draw #{draw.draw_number}</h1>
          <p className="text-slate-500 text-sm">{d.loan?.project_name} · {[d.loan?.city, d.loan?.state].filter(Boolean).join(', ')}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>{money(draw.amount)}</div>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Draw vs approved bid sheet */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm flex items-center gap-1.5" style={{ color: LENDER_NAVY }}>
              <Scale className="w-4 h-4" /> Draw vs. approved budget
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <th className="text-left font-semibold px-4 py-2.5">Line item</th>
                    <th className="text-right font-semibold px-3 py-2.5 hidden sm:table-cell">Approved</th>
                    <th className="text-right font-semibold px-3 py-2.5 hidden md:table-cell">Drawn to date</th>
                    <th className="text-right font-semibold px-3 py-2.5">This draw</th>
                    <th className="text-right font-semibold px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((li, i) => {
                    const over = Number(li.over_budget || 0);
                    const thisDraw = li.this_draw != null ? li.this_draw : (li.amount || 0);
                    return (
                      <tr key={i} className="border-b border-slate-50 last:border-0" data-testid={`review-line-${i}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium" style={{ color: LENDER_NAVY }}>{li.line || li.category}</div>
                          {li.category && li.line !== li.category && <div className="text-[11px] text-slate-400">{li.category}</div>}
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-600 hidden sm:table-cell">{li.approved_budget != null ? money(li.approved_budget) : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-slate-500 hidden md:table-cell">{li.drawn_to_date != null ? money(li.drawn_to_date) : '—'}</td>
                        <td className="px-3 py-2.5 text-right font-medium" style={{ color: LENDER_NAVY }}>{money(thisDraw)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {!hasBidSheet ? <span className="text-slate-300">—</span>
                            : over > 0
                              ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`review-line-over-${i}`}>Over {money(over)}</span>
                              : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Within budget</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {lines.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No line items on this draw.</td></tr>
                  )}
                </tbody>
                {hasBidSheet && (
                  <tfoot>
                    <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                      <td className="px-4 py-3" style={{ color: LENDER_NAVY }}>Total</td>
                      <td className="px-3 py-3 text-right text-slate-600 hidden sm:table-cell">{money(totals.approved)}</td>
                      <td className="px-3 py-3 text-right text-slate-500 hidden md:table-cell">{money(totals.drawn)}</td>
                      <td className="px-3 py-3 text-right" style={{ color: LENDER_BLUE }}>{money(totals.thisDraw)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {/* Contingency summary */}
            {(contUsed > 0 || uncovered > 0) && (
              <div className={`px-4 py-3 text-sm flex items-start gap-2 border-t ${uncovered > 0 ? 'bg-red-50 border-red-100 text-red-700' : 'bg-amber-50 border-amber-100 text-amber-800'}`} data-testid="review-contingency">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {contUsed > 0 && <><strong>{money(contUsed)}</strong> of this draw exceeds approved line budgets and was absorbed by the contingency budget. </>}
                  {uncovered > 0 && <><strong>{money(uncovered)}</strong> is over the approved budget AND available contingency. </>}
                  Approval is still allowed — it's recorded in the audit trail.
                </span>
              </div>
            )}
          </div>

          {/* Documents checklist */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-semibold text-sm mb-3" style={{ color: LENDER_NAVY }}>Documents</div>
            <div className="space-y-2">
              {(draw.documents || []).map((doc) => (
                <div key={doc.key} className="flex items-center gap-2 text-sm">
                  {doc.provided
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                  <span className={doc.provided ? 'text-slate-700' : 'text-amber-700 font-medium'}>{doc.label}</span>
                  {!doc.provided && <span className="text-[11px] text-amber-600">missing</span>}
                </div>
              ))}
            </div>
            {draw.borrower_note && (
              <div className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                <FileText className="w-3.5 h-3.5 inline mr-1 text-slate-400" /> {draw.borrower_note}
              </div>
            )}
          </div>

          {/* Action bar */}
          {d.can_act ? (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              {missing.length > 0 && (
                <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-amber-800" data-testid="draw-outstanding-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{missing.length} document{missing.length > 1 ? 's' : ''} outstanding ({missing.join(', ')}). You can still approve — it will be recorded in the audit trail.</span>
                </div>
              )}
              {showSendBack && (
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="send-back-note"
                  placeholder="What does the borrower need to fix?"
                  className="w-full mb-3 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
              )}
              <div className="flex items-center gap-2">
                {showSendBack ? (
                  <button onClick={sendBack} disabled={busy} data-testid="confirm-send-back"
                    className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
                    <CornerUpLeft className="w-4 h-4" /> Confirm send back
                  </button>
                ) : (
                  <button onClick={() => setShowSendBack(true)} data-testid="draw-send-back"
                    className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
                    <CornerUpLeft className="w-4 h-4" /> Send back
                  </button>
                )}
                <button onClick={approve} disabled={busy} data-testid="draw-approve"
                  className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}>
                  <Check className="w-4 h-4" /> {d.approve_label}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500" data-testid="draw-no-action">
              {draw.status === 'approved' ? 'This draw has been fully approved.'
                : draw.status === 'sent_back' ? 'Sent back — waiting on the borrower to resubmit.'
                : draw.status === 'lo_approved' ? 'Approved by the loan officer — waiting on the construction department.'
                : 'No action available to you on this draw.'}
            </div>
          )}
        </div>

        {/* Audit trail */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 h-fit">
          <div className="font-semibold text-sm mb-3" style={{ color: LENDER_NAVY }}>Audit trail</div>
          <ol className="space-y-3">
            {(d.events || []).map((e) => (
              <li key={e.id} className="flex gap-2.5" data-testid={`audit-${e.type}`}>
                <div className="flex flex-col items-center">
                  {(e.type === 'outstanding_recorded' || e.type === 'budget_flag')
                    ? <AlertTriangle className="w-4 h-4 text-amber-500" />
                    : <Circle className="w-3 h-3 mt-0.5" style={{ color: LENDER_BLUE, fill: LENDER_BLUE }} />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: LENDER_NAVY }}>{EVENT_LABEL[e.type] || e.type}</div>
                  {e.note && <div className="text-xs text-slate-500">{e.note}</div>}
                  <div className="text-[11px] text-slate-400 mt-0.5">{e.actor_name} · {fmtDateTime(e.created_at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </LenderShell>
  );
}
